import type {
  AssetAiSuggestionDecisionApi,
  AssetAiSuggestionResponseDto,
  AssetAiSuggestionTypeApi,
  AssetTypeApi,
  ConfidentialityLevelApi,
} from './models/asset-api.models';
import type { AssetReviewFormValue } from './models/asset-review-form.models';

export type LocalSuggestionDecision = 'pending' | 'accepted' | 'rejected';

export interface AssetReviewFieldDefinition {
  fieldKey: keyof AssetReviewFormValue;
  label: string;
  suggestionTypes: AssetAiSuggestionTypeApi[];
  required?: boolean;
}

/** Single-column review order: suggestions above each control. */
export const ASSET_REVIEW_FIELDS: AssetReviewFieldDefinition[] = [
  { fieldKey: 'title', label: 'Title', suggestionTypes: ['Title'], required: true },
  { fieldKey: 'description', label: 'Description', suggestionTypes: ['Summary'] },
  { fieldKey: 'type', label: 'Type', suggestionTypes: ['AssetType'], required: true },
  { fieldKey: 'industries', label: 'Industries', suggestionTypes: ['Industry'] },
  { fieldKey: 'practices', label: 'Practices', suggestionTypes: ['Practice'] },
  { fieldKey: 'tags', label: 'Tags', suggestionTypes: ['Tag'] },
  {
    fieldKey: 'confidentiality',
    label: 'Confidentiality',
    suggestionTypes: ['Confidentiality'],
    required: true,
  },
  { fieldKey: 'owner', label: 'Owner', suggestionTypes: [], required: true },
  { fieldKey: 'reviewDate', label: 'Review date', suggestionTypes: [], required: true },
  { fieldKey: 'language', label: 'Content language', suggestionTypes: ['Language'] },
  // Related capability is manual metadata only — not in AssetAiSuggestionProjectionService.
  { fieldKey: 'relatedCapability', label: 'Related capability', suggestionTypes: [] },
];

export function groupSuggestionsByType(
  suggestions: AssetAiSuggestionResponseDto[],
): Map<AssetAiSuggestionTypeApi, AssetAiSuggestionResponseDto[]> {
  const map = new Map<AssetAiSuggestionTypeApi, AssetAiSuggestionResponseDto[]>();
  for (const suggestion of suggestions) {
    const list = map.get(suggestion.type) ?? [];
    list.push(suggestion);
    map.set(suggestion.type, list);
  }
  return map;
}

export function suggestionsForField(
  field: AssetReviewFieldDefinition,
  byType: Map<AssetAiSuggestionTypeApi, AssetAiSuggestionResponseDto[]>,
  options?: { excludeReferenceAssetType?: boolean },
): AssetAiSuggestionResponseDto[] {
  const suggestions = field.suggestionTypes.flatMap((type) => byType.get(type) ?? []);
  if (!options?.excludeReferenceAssetType) {
    return suggestions;
  }
  return suggestions.filter(
    (suggestion) => suggestion.type !== 'AssetType' || suggestion.value !== 'Reference',
  );
}

export function serverDecisionToLocal(
  decision: AssetAiSuggestionDecisionApi,
): LocalSuggestionDecision {
  switch (decision) {
    case 'Accepted':
    case 'Applied':
      return 'accepted';
    case 'Rejected':
      return 'rejected';
    default:
      return 'pending';
  }
}

export function isSuggestionUndecidedLocally(
  localDecisions: ReadonlyMap<string, LocalSuggestionDecision>,
  suggestion: AssetAiSuggestionResponseDto,
): boolean {
  return (
    (localDecisions.get(suggestion.id) ?? serverDecisionToLocal(suggestion.decision)) === 'pending'
  );
}

export function hasPendingLocalSuggestions(
  suggestions: AssetAiSuggestionResponseDto[],
  localDecisions: ReadonlyMap<string, LocalSuggestionDecision>,
): boolean {
  return suggestions
    .filter((suggestion) => suggestion.type !== 'GoldenCandidate')
    .some((suggestion) => isSuggestionUndecidedLocally(localDecisions, suggestion));
}

export function applySuggestionPreviewToForm(
  form: AssetReviewFormValue,
  suggestion: AssetAiSuggestionResponseDto,
  industryIdByName?: (name: string) => string | null,
): AssetReviewFormValue {
  const value = suggestion.value.trim();
  if (!value) {
    return form;
  }

  switch (suggestion.type) {
    case 'Title':
      return { ...form, title: value };
    case 'Summary':
      return { ...form, description: value };
    case 'AssetType': {
      const parsed = parseAssetType(value);
      if (parsed === 'Reference') {
        return form;
      }
      return { ...form, type: parsed };
    }
    case 'Industry': {
      const industryId = industryIdByName?.(value);
      if (!industryId) {
        return form;
      }
      return form.industries.includes(industryId)
        ? form
        : { ...form, industries: [...form.industries, industryId] };
    }
    case 'Practice':
      return form.practices.some((item) => item.toLowerCase() === value.toLowerCase())
        ? form
        : { ...form, practices: [...form.practices, value] };
    case 'Tag':
      return form.tags.some((tag) => tag.toLowerCase() === value.toLowerCase())
        ? form
        : { ...form, tags: [...form.tags, value] };
    case 'Confidentiality':
      return { ...form, confidentiality: parseConfidentiality(value) };
    case 'Language':
      return { ...form, language: value };
    case 'RelatedCapability':
      return { ...form, relatedCapability: value };
    default:
      return form;
  }
}

export function collectApplySuggestionIds(
  suggestions: AssetAiSuggestionResponseDto[],
  localDecisions: ReadonlyMap<string, LocalSuggestionDecision>,
): string[] {
  return suggestions
    .filter((suggestion) => {
      if (suggestion.decision === 'Applied' || suggestion.decision === 'Rejected') {
        return false;
      }
      const local = localDecisions.get(suggestion.id) ?? serverDecisionToLocal(suggestion.decision);
      return local === 'accepted';
    })
    .map((suggestion) => suggestion.id);
}

export function collectAcceptSuggestionIds(
  suggestions: AssetAiSuggestionResponseDto[],
  localDecisions: ReadonlyMap<string, LocalSuggestionDecision>,
): string[] {
  return suggestions
    .filter((suggestion) => {
      if (suggestion.decision !== 'Pending') {
        return false;
      }
      const local = localDecisions.get(suggestion.id) ?? 'pending';
      return local === 'accepted';
    })
    .map((suggestion) => suggestion.id);
}

export function collectRejectSuggestionIds(
  suggestions: AssetAiSuggestionResponseDto[],
  localDecisions: ReadonlyMap<string, LocalSuggestionDecision>,
): string[] {
  return suggestions
    .filter((suggestion) => {
      if (suggestion.decision !== 'Pending') {
        return false;
      }
      const local = localDecisions.get(suggestion.id) ?? 'pending';
      return local === 'rejected';
    })
    .map((suggestion) => suggestion.id);
}

function parseAssetType(value: string): AssetTypeApi {
  const normalized = value.replace(/\s+/g, '');
  const types: AssetTypeApi[] = [
    'Template',
    'Reference',
    'Offering',
    'DeliveryOutput',
    'Proposal',
    'PointOfView',
    'Calculator',
    'Accelerator',
    'ArchitectureBlueprint',
    'Other',
  ];
  const match = types.find(
    (type) =>
      type.toLowerCase() === normalized.toLowerCase() || type.toLowerCase() === value.toLowerCase(),
  );
  return match ?? 'Other';
}

function parseConfidentiality(value: string): ConfidentialityLevelApi {
  const levels: ConfidentialityLevelApi[] = ['Public', 'Internal', 'Confidential'];
  return levels.find((level) => level.toLowerCase() === value.toLowerCase()) ?? 'Internal';
}
